import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search, ChevronRight, Loader2, AlertTriangle,
  CheckCircle, XCircle, Shield, Stethoscope,
  FileText, CreditCard, Building2,
} from 'lucide-react';

// ── Types ─────────────────────────────────────────────────────────────────────

interface DoctorRecord {
  name: string;
  registration_no: string;
  father_name?: string;
  qualification?: string;
  year_of_registration?: string;
  state_medical_council?: string;
  state?: string;
  is_blacklisted: boolean;
}

interface DoctorBlacklistRecord {
  name: string;
  registration_no: string;
  state_medical_council?: string;
  suspend_date?: string;
  restore_date?: string;
  remarks?: string;
}

interface DoctorSearchResult {
  total: number;
  records: DoctorRecord[];
  blacklisted: DoctorBlacklistRecord[];
  search_type: string;
  query: string;
}

type VerifyTab = 'doctor' | 'pan' | 'gst' | 'corporate';
type DoctorMode = 'name' | 'registration' | 'smc' | 'advanced' | 'blacklist';

// ── Constants ─────────────────────────────────────────────────────────────────

const SMC_LIST: { id: number; name: string }[] = [
  { id: 1,  name: "Andhra Pradesh Medical Council" },
  { id: 2,  name: "Arunachal Pradesh Medical Council" },
  { id: 3,  name: "Assam Medical Council" },
  { id: 4,  name: "Bihar Medical Council" },
  { id: 5,  name: "Chhattisgarh Medical Council" },
  { id: 6,  name: "Delhi Medical Council" },
  { id: 7,  name: "Goa Medical Council" },
  { id: 8,  name: "Gujarat Medical Council" },
  { id: 9,  name: "Haryana Medical Council" },
  { id: 10, name: "Himachal Pradesh Medical Council" },
  { id: 11, name: "Jammu & Kashmir Medical Council" },
  { id: 12, name: "Jharkhand Medical Council" },
  { id: 13, name: "Karnataka Medical Council" },
  { id: 14, name: "Kerala Medical Council" },
  { id: 15, name: "Madhya Pradesh Medical Council" },
  { id: 16, name: "Maharashtra Medical Council" },
  { id: 17, name: "Manipur Medical Council" },
  { id: 18, name: "Meghalaya Medical Council" },
  { id: 19, name: "Mizoram Medical Council" },
  { id: 20, name: "Nagaland Medical Council" },
  { id: 21, name: "Odisha Council of Medical Registration" },
  { id: 22, name: "Punjab Medical Council" },
  { id: 23, name: "Rajasthan Medical Council" },
  { id: 24, name: "Sikkim Medical Council" },
  { id: 25, name: "Tamil Nadu Medical Council" },
  { id: 26, name: "Telangana State Medical Council" },
  { id: 27, name: "Tripura State Medical Council" },
  { id: 28, name: "Uttar Pradesh Medical Council" },
  { id: 29, name: "Uttarakhand Medical Council" },
  { id: 30, name: "West Bengal Medical Council" },
];

// ── API helpers ───────────────────────────────────────────────────────────────

async function apiGet<T>(path: string): Promise<T> {
  const token = localStorage.getItem('auth_token');
  const res = await fetch(`/api${path}`, {
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  if (!res.ok) {
    const j = await res.json().catch(() => ({}));
    throw new Error(j.detail || `${res.status} ${res.statusText}`);
  }
  return res.json();
}

// ── Shared UI primitives ──────────────────────────────────────────────────────

const inputStyle: React.CSSProperties = {
  background: '#0C1118', border: '1px solid #182030', borderRadius: 8,
  color: '#DCE8F5', fontFamily: 'monospace', fontSize: 11,
  padding: '9px 12px', outline: 'none', width: '100%',
};

const btnStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8,
  background: '#00C8FF18', color: '#00C8FF',
  border: '1px solid #00C8FF44',
  borderRadius: 8, padding: '9px 20px',
  fontFamily: 'monospace', fontSize: 10, fontWeight: 700,
  cursor: 'pointer', letterSpacing: '0.08em', whiteSpace: 'nowrap',
};

function Label({ children }: { children: React.ReactNode }) {
  return (
    <p style={{ color: '#5A7A9A', fontSize: 9, fontFamily: 'monospace', letterSpacing: '0.12em', marginBottom: 5 }}>
      {children}
    </p>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <p style={{ color: '#5A7A9A', fontSize: 9, fontFamily: 'monospace', letterSpacing: '0.18em', marginBottom: 12 }}>
      {children}
    </p>
  );
}

function ErrorCard({ message }: { message: string }) {
  return (
    <div style={{ background: '#0C0508', border: '1px solid #FF1F1F30', borderRadius: 10, padding: '16px 18px', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
      <AlertTriangle size={14} style={{ color: '#FF6B1A', flexShrink: 0, marginTop: 1 }} />
      <p style={{ color: '#FF6B1A', fontFamily: 'monospace', fontSize: 10 }}>{message}</p>
    </div>
  );
}

// ── Doctor search ─────────────────────────────────────────────────────────────

function BlacklistBadge() {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      background: '#FF1F1F18', color: '#FF1F1F',
      border: '1px solid #FF1F1F40',
      fontSize: 8, fontFamily: 'monospace', fontWeight: 700,
      padding: '2px 8px', borderRadius: 99,
    }}>
      <AlertTriangle size={9} /> BLACKLISTED
    </span>
  );
}

function DoctorCard({ doctor }: { doctor: DoctorRecord }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      style={{
        background: doctor.is_blacklisted ? '#100508' : '#0C1118',
        border: `1px solid ${doctor.is_blacklisted ? '#FF1F1F40' : '#182030'}`,
        borderLeft: `3px solid ${doctor.is_blacklisted ? '#FF1F1F' : '#00C8FF'}`,
        borderRadius: 8, padding: '12px 14px',
      }}
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <div>
          <p style={{ color: '#DCE8F5', fontFamily: 'monospace', fontWeight: 700, fontSize: 12 }}>
            {doctor.name || '—'}
          </p>
          {doctor.father_name && (
            <p style={{ color: '#5A7A9A', fontSize: 9, fontFamily: 'monospace', marginTop: 2 }}>
              S/O {doctor.father_name}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {doctor.is_blacklisted && <BlacklistBadge />}
          <span style={{
            background: '#00C8FF18', color: '#00C8FF',
            border: '1px solid #00C8FF30',
            fontSize: 9, fontFamily: 'monospace', fontWeight: 700,
            padding: '2px 10px', borderRadius: 99,
          }}>
            {doctor.registration_no || '—'}
          </span>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
        {[
          { label: 'STATE MEDICAL COUNCIL', value: doctor.state_medical_council },
          { label: 'YEAR',                  value: doctor.year_of_registration },
          { label: 'QUALIFICATION',         value: doctor.qualification },
          { label: 'STATE',                 value: doctor.state },
        ].filter(f => f.value).map(f => (
          <div key={f.label} style={{ background: '#090D13', borderRadius: 5, padding: '6px 8px' }}>
            <p style={{ color: '#3A5A7A', fontSize: 7, fontFamily: 'monospace', letterSpacing: '0.08em' }}>{f.label}</p>
            <p style={{ color: '#A0B8CC', fontSize: 9, fontFamily: 'monospace', marginTop: 2 }}>{f.value}</p>
          </div>
        ))}
      </div>
    </motion.div>
  );
}

function BlacklistTable({ records }: { records: DoctorBlacklistRecord[] }) {
  if (!records.length) {
    return (
      <div style={{ background: '#0C1118', border: '1px solid #182030', borderRadius: 10, padding: '28px', textAlign: 'center' }}>
        <CheckCircle size={28} style={{ color: '#00C853', margin: '0 auto 10px' }} />
        <p style={{ color: '#00C853', fontFamily: 'monospace', fontSize: 11, fontWeight: 700 }}>No blacklisted doctors found</p>
        <p style={{ color: '#3A5A7A', fontSize: 9, fontFamily: 'monospace', marginTop: 4 }}>The filtered search returned no matches.</p>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {records.map((bl, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, x: -4 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: i * 0.025 }}
          style={{
            background: '#100508', border: '1px solid #FF1F1F30',
            borderLeft: '3px solid #FF1F1F', borderRadius: 8, padding: '12px 14px',
          }}
        >
          <div className="flex items-center justify-between gap-3 mb-2">
            <div>
              <p style={{ color: '#DCE8F5', fontFamily: 'monospace', fontWeight: 700, fontSize: 11 }}>{bl.name}</p>
              {bl.state_medical_council && (
                <p style={{ color: '#5A7A9A', fontSize: 9, fontFamily: 'monospace', marginTop: 1 }}>{bl.state_medical_council}</p>
              )}
            </div>
            <span style={{ background: '#FF1F1F18', color: '#FF1F1F', border: '1px solid #FF1F1F30', fontSize: 9, fontFamily: 'monospace', fontWeight: 700, padding: '2px 10px', borderRadius: 99 }}>
              {bl.registration_no}
            </span>
          </div>
          <div style={{ display: 'flex', gap: 16 }}>
            {bl.suspend_date && (
              <div>
                <p style={{ color: '#3A5A7A', fontSize: 7, fontFamily: 'monospace' }}>SUSPENDED</p>
                <p style={{ color: '#FF6B1A', fontSize: 9, fontFamily: 'monospace' }}>{bl.suspend_date}</p>
              </div>
            )}
            {bl.restore_date && (
              <div>
                <p style={{ color: '#3A5A7A', fontSize: 7, fontFamily: 'monospace' }}>RESTORED</p>
                <p style={{ color: '#00C853', fontSize: 9, fontFamily: 'monospace' }}>{bl.restore_date}</p>
              </div>
            )}
            {bl.remarks && (
              <div style={{ flex: 1 }}>
                <p style={{ color: '#3A5A7A', fontSize: 7, fontFamily: 'monospace' }}>REMARKS</p>
                <p style={{ color: '#A0B8CC', fontSize: 9, fontFamily: 'monospace' }}>{bl.remarks}</p>
              </div>
            )}
          </div>
        </motion.div>
      ))}
    </div>
  );
}

function DoctorTab() {
  const [mode, setMode] = useState<DoctorMode>('name');

  // Search form state
  const [name, setName]         = useState('');
  const [regNo, setRegNo]       = useState('');
  const [smcId, setSmcId]       = useState('');
  const [year, setYear]         = useState('');
  // Blacklist filter state
  const [blSmcId, setBlSmcId]   = useState('');
  const [blRegNo, setBlRegNo]   = useState('');

  const [searchKey, setSearchKey] = useState<string | null>(null);
  const [blKey, setBlKey]         = useState<string | null>(null);

  const { data, isFetching, error } = useQuery<DoctorSearchResult>({
    queryKey: ['doctor-search', searchKey],
    queryFn: () => {
      if (!searchKey) throw new Error('No search');
      return apiGet<DoctorSearchResult>(searchKey);
    },
    enabled: !!searchKey,
    staleTime: 5 * 60_000,
    retry: false,
  });

  const { data: blData, isFetching: blFetching, error: blError } = useQuery<DoctorBlacklistRecord[]>({
    queryKey: ['doctor-blacklist', blKey],
    queryFn: () => {
      if (!blKey) throw new Error('No key');
      return apiGet<DoctorBlacklistRecord[]>(blKey);
    },
    enabled: !!blKey,
    staleTime: 5 * 60_000,
    retry: false,
  });

  function runSearch() {
    const params = new URLSearchParams();
    if (mode === 'name' && name.trim()) {
      params.set('name', name.trim());
    } else if (mode === 'registration' && regNo.trim()) {
      params.set('registration_no', regNo.trim());
    } else if (mode === 'smc' && smcId) {
      params.set('smc_id', smcId);
    } else if (mode === 'advanced') {
      if (name.trim())  params.set('name', name.trim());
      if (regNo.trim()) params.set('registration_no', regNo.trim());
      if (smcId)        params.set('smc_id', smcId);
      if (year.trim())  params.set('year', year.trim());
    }
    if (!params.toString()) return;
    setSearchKey(`/v1/verify/doctor?${params.toString()}`);
  }

  function runBlacklist() {
    const params = new URLSearchParams();
    if (blSmcId) params.set('smc_id', blSmcId);
    if (blRegNo.trim()) params.set('registration_no', blRegNo.trim());
    setBlKey(`/v1/verify/doctor/blacklist?${params.toString()}`);
  }

  const MODES: { key: DoctorMode; label: string }[] = [
    { key: 'name',         label: 'By Name' },
    { key: 'registration', label: 'By Reg. No.' },
    { key: 'smc',          label: 'By State Council' },
    { key: 'advanced',     label: 'Advanced' },
    { key: 'blacklist',    label: 'Blacklist' },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Mode tabs */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {MODES.map(m => (
          <button
            key={m.key}
            onClick={() => setMode(m.key)}
            style={{
              background: mode === m.key ? '#00C8FF18' : '#0C1118',
              color: mode === m.key ? '#00C8FF' : '#5A7A9A',
              border: `1px solid ${mode === m.key ? '#00C8FF44' : '#182030'}`,
              borderRadius: 6, padding: '6px 14px',
              fontFamily: 'monospace', fontSize: 9, fontWeight: 700,
              cursor: 'pointer', letterSpacing: '0.08em',
            }}
          >
            {m.label}
          </button>
        ))}
      </div>

      {/* Search form — register / name / smc / advanced */}
      {mode !== 'blacklist' && (
        <div
          style={{
            background: '#090D13', border: '1px solid #182030',
            borderRadius: 12, padding: '16px 18px',
          }}
        >
          <SectionTitle>SEARCH PARAMETERS</SectionTitle>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
            {(mode === 'name' || mode === 'advanced') && (
              <div>
                <Label>DOCTOR NAME</Label>
                <input
                  value={name} onChange={e => setName(e.target.value)}
                  placeholder="e.g. Arjun Kumar"
                  style={inputStyle}
                  onKeyDown={e => e.key === 'Enter' && runSearch()}
                />
              </div>
            )}
            {(mode === 'registration' || mode === 'advanced') && (
              <div>
                <Label>REGISTRATION NUMBER</Label>
                <input
                  value={regNo} onChange={e => setRegNo(e.target.value)}
                  placeholder="e.g. 140402"
                  style={inputStyle}
                  onKeyDown={e => e.key === 'Enter' && runSearch()}
                />
              </div>
            )}
            {(mode === 'smc' || mode === 'advanced') && (
              <div>
                <Label>STATE MEDICAL COUNCIL</Label>
                <select
                  value={smcId} onChange={e => setSmcId(e.target.value)}
                  style={{ ...inputStyle, appearance: 'none', cursor: 'pointer' }}
                >
                  <option value="">— Select Council —</option>
                  {SMC_LIST.map(s => (
                    <option key={s.id} value={String(s.id)}>{s.name}</option>
                  ))}
                </select>
              </div>
            )}
            {mode === 'advanced' && (
              <div>
                <Label>YEAR OF REGISTRATION</Label>
                <input
                  value={year} onChange={e => setYear(e.target.value)}
                  placeholder="e.g. 2021"
                  style={inputStyle}
                  onKeyDown={e => e.key === 'Enter' && runSearch()}
                  maxLength={4}
                />
              </div>
            )}
          </div>
          <div style={{ marginTop: 14 }}>
            <button onClick={runSearch} disabled={isFetching} style={{ ...btnStyle, opacity: isFetching ? 0.6 : 1 }}>
              {isFetching
                ? <><Loader2 size={12} className="animate-spin" /> SEARCHING…</>
                : <><Search size={12} /> SEARCH NMC REGISTER</>}
            </button>
          </div>
        </div>
      )}

      {/* Blacklist search form */}
      {mode === 'blacklist' && (
        <div style={{ background: '#090D13', border: '1px solid #FF1F1F20', borderRadius: 12, padding: '16px 18px' }}>
          <SectionTitle>BLACKLIST FILTER</SectionTitle>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <Label>STATE MEDICAL COUNCIL (optional)</Label>
              <select
                value={blSmcId} onChange={e => setBlSmcId(e.target.value)}
                style={{ ...inputStyle, appearance: 'none' }}
              >
                <option value="">— All Councils —</option>
                {SMC_LIST.map(s => (
                  <option key={s.id} value={String(s.id)}>{s.name}</option>
                ))}
              </select>
            </div>
            <div>
              <Label>REGISTRATION NUMBER (optional)</Label>
              <input
                value={blRegNo} onChange={e => setBlRegNo(e.target.value)}
                placeholder="e.g. 741"
                style={inputStyle}
              />
            </div>
          </div>
          <div style={{ marginTop: 14 }}>
            <button onClick={runBlacklist} disabled={blFetching} style={{
              ...btnStyle,
              background: '#FF1F1F18', color: '#FF1F1F', border: '1px solid #FF1F1F44',
              opacity: blFetching ? 0.6 : 1,
            }}>
              {blFetching
                ? <><Loader2 size={12} className="animate-spin" /> LOADING…</>
                : <><AlertTriangle size={12} /> FETCH BLACKLIST</>}
            </button>
          </div>
        </div>
      )}

      {/* Results */}
      <AnimatePresence mode="wait">
        {mode !== 'blacklist' && error && (
          <motion.div key="err" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <ErrorCard message={(error as Error).message} />
          </motion.div>
        )}

        {mode !== 'blacklist' && data && (
          <motion.div key="results" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
          >
            {/* Summary bar */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <SectionTitle>RESULTS FROM NMC INDIAN MEDICAL REGISTER</SectionTitle>
                <p style={{ color: '#DCE8F5', fontFamily: 'monospace', fontSize: 10 }}>
                  {data.total} record{data.total !== 1 ? 's' : ''} found
                  {data.blacklisted.length > 0 && (
                    <span style={{ color: '#FF1F1F', marginLeft: 10, fontWeight: 700 }}>
                      · {data.blacklisted.length} BLACKLISTED
                    </span>
                  )}
                </p>
              </div>
            </div>

            {data.records.length === 0 ? (
              <div style={{ background: '#0C1118', border: '1px solid #182030', borderRadius: 10, padding: '28px', textAlign: 'center' }}>
                <XCircle size={28} style={{ color: '#5A7A9A', margin: '0 auto 10px' }} />
                <p style={{ color: '#5A7A9A', fontFamily: 'monospace', fontSize: 10 }}>No records found in the Indian Medical Register.</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {data.records.map((doc, i) => (
                  <DoctorCard key={i} doctor={doc} />
                ))}
              </div>
            )}
          </motion.div>
        )}

        {mode === 'blacklist' && blError && (
          <motion.div key="blerr" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <ErrorCard message={(blError as Error).message} />
          </motion.div>
        )}

        {mode === 'blacklist' && blData && (
          <motion.div key="blresults" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
          >
            <SectionTitle>BLACKLISTED DOCTORS — NMC REGISTER</SectionTitle>
            <p style={{ color: '#DCE8F5', fontFamily: 'monospace', fontSize: 10, marginBottom: 4 }}>
              {blData.length} blacklisted doctor{blData.length !== 1 ? 's' : ''}
            </p>
            <BlacklistTable records={blData} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ── PAN verification tab ──────────────────────────────────────────────────────

function PANTab() {
  const [pan, setPan]   = useState('');
  const [name, setName] = useState('');
  const [queryKey, setQueryKey] = useState<string | null>(null);

  const { data, isFetching, error } = useQuery<Record<string, unknown>>({
    queryKey: ['pan-verify', queryKey],
    queryFn: () => apiGet<Record<string, unknown>>(queryKey!),
    enabled: !!queryKey,
    staleTime: 5 * 60_000,
    retry: false,
  });

  function run() {
    const p = pan.trim().toUpperCase();
    if (p.length !== 10) return;
    const params = new URLSearchParams();
    if (name.trim()) params.set('name', name.trim());
    setQueryKey(`/v1/verify/pan/${p}?${params.toString()}`);
  }

  const status = data?.status as string | undefined;
  const statusColor = status === 'VALID' ? '#00C853' : status === 'INVALID' ? '#FF1F1F' : '#F0B429';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ background: '#090D13', border: '1px solid #182030', borderRadius: 12, padding: '16px 18px' }}>
        <SectionTitle>PAN CARD VERIFICATION</SectionTitle>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div>
            <Label>PAN NUMBER *</Label>
            <input
              value={pan} onChange={e => setPan(e.target.value.toUpperCase())}
              placeholder="ABCDE1234F"
              maxLength={10}
              style={{ ...inputStyle, textTransform: 'uppercase', letterSpacing: '0.1em' }}
              onKeyDown={e => e.key === 'Enter' && run()}
            />
          </div>
          <div>
            <Label>EXPECTED NAME (optional)</Label>
            <input
              value={name} onChange={e => setName(e.target.value)}
              placeholder="For name-match scoring"
              style={inputStyle}
              onKeyDown={e => e.key === 'Enter' && run()}
            />
          </div>
        </div>
        <div style={{ marginTop: 14 }}>
          <button onClick={run} disabled={pan.length !== 10 || isFetching} style={{ ...btnStyle, opacity: (pan.length !== 10 || isFetching) ? 0.5 : 1 }}>
            {isFetching ? <><Loader2 size={12} className="animate-spin" /> VERIFYING…</> : <><Search size={12} /> VERIFY PAN</>}
          </button>
        </div>
      </div>

      <AnimatePresence mode="wait">
        {error && (
          <motion.div key="err" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <ErrorCard message={(error as Error).message} />
          </motion.div>
        )}
        {data && (
          <motion.div key="data" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
            style={{ background: '#0C1118', border: `1px solid ${statusColor}30`, borderRadius: 12, padding: '18px 20px' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
              {status === 'VALID'
                ? <CheckCircle size={18} style={{ color: '#00C853' }} />
                : <XCircle size={18} style={{ color: '#FF1F1F' }} />}
              <p style={{ color: statusColor, fontFamily: 'monospace', fontWeight: 800, fontSize: 14, letterSpacing: '0.1em' }}>
                {status ?? 'UNKNOWN'}
              </p>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
              {Object.entries(data).filter(([k]) => k !== 'status').map(([k, v]) => (
                <div key={k} style={{ background: '#090D13', borderRadius: 6, padding: '8px 10px' }}>
                  <p style={{ color: '#3A5A7A', fontSize: 7, fontFamily: 'monospace', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 3 }}>{k.replace(/_/g, ' ')}</p>
                  <p style={{ color: '#A0B8CC', fontSize: 10, fontFamily: 'monospace' }}>{String(v ?? '—')}</p>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ── GST verification tab ──────────────────────────────────────────────────────

function GSTTab() {
  const [gstin, setGstin]       = useState('');
  const [queryKey, setQueryKey] = useState<string | null>(null);

  const { data, isFetching, error } = useQuery<Record<string, unknown>>({
    queryKey: ['gst-verify', queryKey],
    queryFn: () => apiGet<Record<string, unknown>>(queryKey!),
    enabled: !!queryKey,
    staleTime: 5 * 60_000,
    retry: false,
  });

  function run() {
    const g = gstin.trim().toUpperCase();
    if (g.length !== 15) return;
    setQueryKey(`/v1/verify/gst/${g}`);
  }

  const status = data?.status as string | undefined;
  const statusColor = status === 'ACTIVE' ? '#00C853' : status === 'CANCELLED' || status === 'SUSPENDED' ? '#FF1F1F' : '#F0B429';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ background: '#090D13', border: '1px solid #182030', borderRadius: 12, padding: '16px 18px' }}>
        <SectionTitle>GST REGISTRATION VERIFICATION</SectionTitle>
        <div style={{ maxWidth: 360 }}>
          <Label>GSTIN *</Label>
          <div style={{ display: 'flex', gap: 10 }}>
            <input
              value={gstin} onChange={e => setGstin(e.target.value.toUpperCase())}
              placeholder="22AAAAA0000A1Z5"
              maxLength={15}
              style={{ ...inputStyle, textTransform: 'uppercase', letterSpacing: '0.08em' }}
              onKeyDown={e => e.key === 'Enter' && run()}
            />
            <button onClick={run} disabled={gstin.length !== 15 || isFetching}
              style={{ ...btnStyle, opacity: (gstin.length !== 15 || isFetching) ? 0.5 : 1, flexShrink: 0 }}
            >
              {isFetching ? <Loader2 size={12} className="animate-spin" /> : <Search size={12} />}
              VERIFY
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence mode="wait">
        {error && (
          <motion.div key="err" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <ErrorCard message={(error as Error).message} />
          </motion.div>
        )}
        {data && (
          <motion.div key="data" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
            style={{ background: '#0C1118', border: `1px solid ${statusColor}30`, borderRadius: 12, padding: '18px 20px' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
              {status === 'ACTIVE'
                ? <CheckCircle size={18} style={{ color: '#00C853' }} />
                : <XCircle size={18} style={{ color: '#FF1F1F' }} />}
              <p style={{ color: statusColor, fontFamily: 'monospace', fontWeight: 800, fontSize: 14 }}>
                {status ?? 'UNKNOWN'}
              </p>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
              {Object.entries(data).filter(([k]) => k !== 'status').map(([k, v]) => (
                <div key={k} style={{ background: '#090D13', borderRadius: 6, padding: '8px 10px' }}>
                  <p style={{ color: '#3A5A7A', fontSize: 7, fontFamily: 'monospace', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 3 }}>{k.replace(/_/g, ' ')}</p>
                  <p style={{ color: '#A0B8CC', fontSize: 10, fontFamily: 'monospace', wordBreak: 'break-all' }}>{String(v ?? '—')}</p>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ── Corporate / MCA21 tab ────────────────────────────────────────────────────

function CompanyCard({ company }: { company: Record<string, unknown> }) {
  const status = String(company.status ?? '');
  const statusColor = status === 'ACTIVE' ? '#00C853' : status === 'STRUCK_OFF' ? '#FF1F1F' : '#F0B429';
  const directors = (company.co_directors as string[]) ?? [];

  return (
    <div style={{
      background: '#0C1118',
      border: `1px solid ${statusColor}25`,
      borderLeft: `3px solid ${statusColor}`,
      borderRadius: 8, padding: '12px 14px',
    }}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <p style={{ color: '#DCE8F5', fontFamily: 'monospace', fontWeight: 700, fontSize: 12 }}>
            {String(company.name || '—')}
          </p>
          <p style={{ color: '#5A7A9A', fontSize: 9, fontFamily: 'monospace', marginTop: 2 }}>
            {String(company.cin || '—')}
          </p>
        </div>
        <span style={{
          background: `${statusColor}18`, color: statusColor,
          border: `1px solid ${statusColor}30`,
          fontSize: 8, fontFamily: 'monospace', fontWeight: 700,
          padding: '2px 10px', borderRadius: 99, flexShrink: 0,
        }}>
          {status || 'UNKNOWN'}
        </span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: directors.length ? 10 : 0 }}>
        {[
          { label: 'REG. DATE',     value: company.registration_date },
          { label: 'AUTH. CAPITAL', value: company.authorized_capital },
          { label: 'PAID-UP',       value: company.paid_up_capital },
          { label: 'ADDRESS',       value: company.address },
          { label: 'NATURE',        value: company.nature_of_business },
          { label: 'EMAIL',         value: company.email },
        ].filter(f => f.value).map(f => (
          <div key={f.label} style={{ background: '#090D13', borderRadius: 5, padding: '6px 8px' }}>
            <p style={{ color: '#3A5A7A', fontSize: 7, fontFamily: 'monospace', letterSpacing: '0.08em' }}>{f.label}</p>
            <p style={{ color: '#A0B8CC', fontSize: 9, fontFamily: 'monospace', marginTop: 2, wordBreak: 'break-all' }}>
              {String(f.value)}
            </p>
          </div>
        ))}
      </div>

      {directors.length > 0 && (
        <div>
          <p style={{ color: '#3A5A7A', fontSize: 7, fontFamily: 'monospace', letterSpacing: '0.08em', marginBottom: 6 }}>
            DIRECTORS / SIGNATORIES
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
            {directors.map((d, i) => (
              <span key={i} style={{
                background: '#A855F718', color: '#A855F7',
                border: '1px solid #A855F730',
                fontSize: 9, fontFamily: 'monospace',
                padding: '2px 8px', borderRadius: 99,
              }}>
                {d}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function CorporateTab() {
  const [name, setName]   = useState('');
  const [cin, setCin]     = useState('');
  const [din, setDin]     = useState('');
  const [queryKey, setQK] = useState<string | null>(null);

  const { data, isFetching, error } = useQuery<Record<string, unknown>>({
    queryKey: ['corporate-verify', queryKey],
    queryFn: () => apiGet<Record<string, unknown>>(queryKey!),
    enabled: !!queryKey,
    staleTime: 5 * 60_000,
    retry: false,
  });

  function run() {
    const params = new URLSearchParams();
    if (cin.trim())  params.set('cin', cin.trim().toUpperCase());
    else if (name.trim()) params.set('name', name.trim());
    else if (din.trim())  params.set('din', din.trim());
    else return;
    setQK(`/v1/verify/corporate?${params.toString()}`);
  }

  // Parse response — may be MCA21Result or InstaFinancials result
  const companies: Record<string, unknown>[] = (() => {
    if (!data) return [];
    if (data.status === 'NOT_FOUND') return [];
    // MCA21Result has companies_as_director
    if (Array.isArray(data.companies_as_director)) return data.companies_as_director as Record<string, unknown>[];
    // Wrapped mca21 result
    const mca = data.mca21 as Record<string, unknown> | undefined;
    if (mca && Array.isArray(mca.companies_as_director)) return mca.companies_as_director as Record<string, unknown>[];
    return [];
  })();

  const mca = (data?.mca21 ?? data) as Record<string, unknown> | undefined;
  const activeCount = Number(mca?.active_directorships ?? 0);
  const struckCount = Number(mca?.struck_off_connections ?? 0);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ background: '#090D13', border: '1px solid #182030', borderRadius: 12, padding: '16px 18px' }}>
        <SectionTitle>COMPANY / DIRECTOR LOOKUP — MCA21</SectionTitle>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
          <div>
            <Label>CIN / COMPANY NAME</Label>
            <input
              value={cin || name}
              onChange={e => {
                const v = e.target.value;
                if (/^[LU]\d/i.test(v) || /^\d/.test(v)) { setCin(v.toUpperCase()); setName(''); }
                else { setName(v); setCin(''); }
              }}
              placeholder="L17110MH1973PLC… or company name"
              style={{ ...inputStyle, textTransform: cin ? 'uppercase' : 'none' }}
              onKeyDown={e => e.key === 'Enter' && run()}
            />
          </div>
          <div>
            <Label>DIN (Director ID)</Label>
            <input
              value={din} onChange={e => setDin(e.target.value.replace(/\D/g, '').slice(0, 8))}
              placeholder="e.g. 00000001"
              style={inputStyle}
              onKeyDown={e => e.key === 'Enter' && run()}
            />
          </div>
        </div>
        <p style={{ color: '#3A5A7A', fontSize: 8, fontFamily: 'monospace', marginTop: 8 }}>
          Provide CIN (starts with L/U), company name, or Director Identification Number.
        </p>
        <div style={{ marginTop: 12 }}>
          <button
            onClick={run}
            disabled={(!name.trim() && !cin.trim() && !din.trim()) || isFetching}
            style={{ ...btnStyle, opacity: (!name.trim() && !cin.trim() && !din.trim()) || isFetching ? 0.5 : 1 }}
          >
            {isFetching
              ? <><Loader2 size={12} className="animate-spin" /> SEARCHING…</>
              : <><Search size={12} /> LOOKUP MCA21</>}
          </button>
        </div>
      </div>

      <AnimatePresence mode="wait">
        {error && (
          <motion.div key="err" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <ErrorCard message={(error as Error).message} />
          </motion.div>
        )}

        {data && data.status === 'NOT_FOUND' && (
          <motion.div key="nf" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
            <div style={{ background: '#0C1118', border: '1px solid #182030', borderRadius: 10, padding: '28px', textAlign: 'center' }}>
              <XCircle size={28} style={{ color: '#5A7A9A', margin: '0 auto 10px' }} />
              <p style={{ color: '#5A7A9A', fontFamily: 'monospace', fontSize: 10 }}>
                No company found in MCA21 for the provided identifier.
              </p>
            </div>
          </motion.div>
        )}

        {companies.length > 0 && (
          <motion.div key="results" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
            style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
          >
            {/* Summary */}
            <div style={{ display: 'flex', gap: 12 }}>
              {[
                { label: 'TOTAL COMPANIES',    value: companies.length,  color: '#A855F7' },
                { label: 'ACTIVE DIRS.',        value: activeCount,       color: '#00C853' },
                { label: 'STRUCK-OFF',          value: struckCount,       color: struckCount > 2 ? '#FF1F1F' : '#F0B429' },
              ].map(m => (
                <div key={m.label} style={{ background: '#0C1118', border: `1px solid ${m.color}20`, borderRadius: 8, padding: '10px 14px', flex: 1 }}>
                  <p style={{ color: m.color, fontFamily: 'monospace', fontWeight: 900, fontSize: 20 }}>{m.value}</p>
                  <p style={{ color: '#5A7A9A', fontSize: 8, fontFamily: 'monospace', letterSpacing: '0.08em', marginTop: 3 }}>{m.label}</p>
                </div>
              ))}
            </div>

            <SectionTitle>COMPANIES ({companies.length})</SectionTitle>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {companies.map((c, i) => (
                <motion.div key={i} initial={{ opacity: 0, x: -6 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.04 }}>
                  <CompanyCard company={c} />
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

const TABS: { key: VerifyTab; label: string; icon: React.ElementType; color: string }[] = [
  { key: 'doctor',    label: 'Doctor / NMC',    icon: Stethoscope, color: '#00C8FF' },
  { key: 'pan',       label: 'PAN Card',         icon: CreditCard,  color: '#F0B429' },
  { key: 'gst',       label: 'GST / GSTIN',      icon: FileText,    color: '#00C853' },
  { key: 'corporate', label: 'Corporate / MCA21', icon: Building2,   color: '#A855F7' },
];

export default function VerifyPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<VerifyTab>('doctor');

  return (
    <div style={{ background: '#050709', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Nav */}
      <div style={{ background: '#090D13', borderBottom: '1px solid #182030' }}>
        <div className="max-w-5xl mx-auto px-6 py-3 flex items-center gap-3">
          <button onClick={() => navigate('/app/profiler')}
            style={{ color: '#5A7A9A', fontFamily: 'monospace', fontSize: 10, background: 'none', border: 'none', cursor: 'pointer' }}>
            PROFILER
          </button>
          <ChevronRight size={10} style={{ color: '#3A5A7A' }} />
          <span style={{ color: '#DCE8F5', fontFamily: 'monospace', fontSize: 10 }}>IDENTIFIER VERIFICATION</span>
        </div>
      </div>

      <div className="flex-1 max-w-5xl mx-auto w-full px-6 py-6" style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        {/* Page header */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            <Shield size={16} style={{ color: '#A855F7' }} />
            <p style={{ color: '#DCE8F5', fontFamily: 'monospace', fontWeight: 800, fontSize: 16, letterSpacing: '0.1em' }}>
              IDENTIFIER VERIFICATION
            </p>
          </div>
          <p style={{ color: '#5A7A9A', fontFamily: 'monospace', fontSize: 9, letterSpacing: '0.06em' }}>
            Direct lookups against government registries — NMC Indian Medical Register · PAN · GST · MCA21
          </p>
        </div>

        {/* Tab bar */}
        <div style={{ display: 'flex', gap: 8, borderBottom: '1px solid #182030', paddingBottom: 0 }}>
          {TABS.map(tab => {
            const Icon = tab.icon;
            const active = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 7,
                  background: 'none', border: 'none', cursor: 'pointer',
                  padding: '10px 16px',
                  borderBottom: active ? `2px solid ${tab.color}` : '2px solid transparent',
                  color: active ? tab.color : '#5A7A9A',
                  fontFamily: 'monospace', fontSize: 10, fontWeight: active ? 700 : 400,
                  letterSpacing: '0.08em',
                  transition: 'all 0.15s',
                  marginBottom: -1,
                }}
              >
                <Icon size={12} />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Tab content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
          >
            {activeTab === 'doctor'    && <DoctorTab />}
            {activeTab === 'pan'       && <PANTab />}
            {activeTab === 'gst'       && <GSTTab />}
            {activeTab === 'corporate' && <CorporateTab />}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}
